import { useState, useEffect, useMemo } from "react";
import { useRoute, useLocation } from "wouter";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, FileText, Shield, Loader2, ChevronDown, ChevronUp, Save, Truck, Users, Plus, Trash2 } from "lucide-react";
import { usePolicy, useUpdatePolicy, useCarriers, useFlintProducers } from "@/lib/api";
import { SearchableSelect } from "@/components/searchable-select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { formatPolicyNumber, formatCompanyName, formatDollar, unformatDollar, toIsoDate, fromIsoDate } from "@/lib/formatters";
import { getPolicyErrors, hasErrors, scrollToFirstError } from "@/lib/validation";
import { FieldError } from "@/components/FieldError";
import { TRUCK_TYPES, US_STATES, type PolicyCoverages, type PolicyVehicle, type PolicyTrailer, type PolicyDriver } from "@shared/schema";
import { computeTotalPremium, hasAnyBreakdown } from "@shared/premium";
import {
  POLICY_STATUS_OPTIONS,
  POLICY_SUBSTATUS_OPTIONS,
} from "@/components/insured/policies/policy-status";

const SUBSTATUS_NONE = "__none__";

const POLICY_TYPES = [
  "Commercial Auto",
  "General Liability",
  "Cargo",
  "Physical Damage",
  "Workers Compensation",
  "Umbrella",
  "Motor Truck Cargo",
  "Non-Trucking Liability",
];

const AUTO_TYPE_OPTIONS = [
  { value: "any", label: "Any Auto" },
  { value: "all_owned", label: "All Owned" },
  { value: "scheduled", label: "Scheduled" },
  { value: "hired", label: "Hired" },
  { value: "non_owned", label: "Non-Owned" },
] as const;

const AGGREGATE_OPTIONS = [
  { value: "policy", label: "Policy" },
  { value: "project", label: "Project" },
  { value: "loc", label: "Location" },
] as const;

const defaultCoverages: Required<PolicyCoverages> = {
  generalLiability: {
    eachOccurrence: "$1,000,000",
    damageToRented: "$100,000",
    medExp: "$5,000",
    personalAdvInjury: "$1,000,000",
    generalAggregate: "$2,000,000",
    productsCompOpAgg: "$2,000,000",
    claimsMade: false,
    aggregateAppliesPer: "policy",
  },
  autoLiability: {
    combinedSingleLimit: "$1,000,000",
    bodilyInjuryPerPerson: "",
    bodilyInjuryPerAccident: "",
    propertyDamage: "",
    autoType: "any",
  },
  nonTruckingLiability: {
    limit: "",
  },
  hiredAutoLiability: {
    combinedSingleLimit: "",
  },
  hiredNonOwnedLiability: {
    limit: "",
  },
  umbrella: {
    eachOccurrence: "$5,000,000",
    aggregate: "$5,000,000",
    isExcess: false,
    deductible: "",
  },
  workersComp: {
    perStatute: true,
    elEachAccident: "$500,000",
    elDiseaseEaEmployee: "$500,000",
    elDiseasePolicyLimit: "$500,000",
  },
  cargo: {
    limit: "$100,000",
    deductible: "$1,000",
  },
  onHook: {
    limit: "",
    deductible: "",
  },
  physicalDamage: {
    comprehensiveDeductible: "$1,000",
    collisionDeductible: "$1,000",
  },
  trailerInterchange: {
    limit: "",
    deductible: "",
  },
  nonOwnedTrailerPhysicalDamage: {
    limit: "",
    deductible: "",
  },
  rentalReimbursement: {
    perDay: "",
    maxPayout: "",
    appliesTo: "",
  },
  nonOwnedAuto: {
    limit: "",
  },
};

function mergeCoverages(existing: PolicyCoverages | null | undefined): Required<PolicyCoverages> {
  if (!existing) return { ...defaultCoverages };
  return {
    generalLiability: existing.generalLiability
      ? { ...defaultCoverages.generalLiability, ...existing.generalLiability }
      : defaultCoverages.generalLiability,
    autoLiability: existing.autoLiability
      ? { ...defaultCoverages.autoLiability, ...existing.autoLiability }
      : defaultCoverages.autoLiability,
    nonTruckingLiability: existing.nonTruckingLiability
      ? { ...defaultCoverages.nonTruckingLiability, ...existing.nonTruckingLiability }
      : defaultCoverages.nonTruckingLiability,
    hiredAutoLiability: existing.hiredAutoLiability
      ? { ...defaultCoverages.hiredAutoLiability, ...existing.hiredAutoLiability }
      : defaultCoverages.hiredAutoLiability,
    hiredNonOwnedLiability: existing.hiredNonOwnedLiability
      ? { ...defaultCoverages.hiredNonOwnedLiability, ...existing.hiredNonOwnedLiability }
      : defaultCoverages.hiredNonOwnedLiability,
    umbrella: existing.umbrella
      ? { ...defaultCoverages.umbrella, ...existing.umbrella }
      : defaultCoverages.umbrella,
    workersComp: existing.workersComp
      ? { ...defaultCoverages.workersComp, ...existing.workersComp }
      : defaultCoverages.workersComp,
    cargo: existing.cargo
      ? { ...defaultCoverages.cargo, ...existing.cargo }
      : defaultCoverages.cargo,
    onHook: existing.onHook
      ? { ...defaultCoverages.onHook, ...existing.onHook }
      : defaultCoverages.onHook,
    physicalDamage: existing.physicalDamage
      ? { ...defaultCoverages.physicalDamage, ...existing.physicalDamage }
      : defaultCoverages.physicalDamage,
    trailerInterchange: existing.trailerInterchange
      ? { ...defaultCoverages.trailerInterchange, ...existing.trailerInterchange }
      : defaultCoverages.trailerInterchange,
    nonOwnedTrailerPhysicalDamage: existing.nonOwnedTrailerPhysicalDamage
      ? { ...defaultCoverages.nonOwnedTrailerPhysicalDamage, ...existing.nonOwnedTrailerPhysicalDamage }
      : defaultCoverages.nonOwnedTrailerPhysicalDamage,
    rentalReimbursement: existing.rentalReimbursement
      ? { ...defaultCoverages.rentalReimbursement, ...existing.rentalReimbursement }
      : defaultCoverages.rentalReimbursement,
    nonOwnedAuto: existing.nonOwnedAuto
      ? { ...defaultCoverages.nonOwnedAuto, ...existing.nonOwnedAuto }
      : defaultCoverages.nonOwnedAuto,
  };
}

export default function PolicyEdit() {
  const [, params] = useRoute("/policies/:id/edit");
  const policyId = params?.id || "";
  const { data: policy, isLoading, isError } = usePolicy(policyId);
  const { data: carrierList } = useCarriers();
  const { data: flintProducers } = useFlintProducers();
  const producerOptions = useMemo(
    () => (flintProducers ?? []).map((p) => ({ value: p.fullName, label: p.fullName })),
    [flintProducers],
  );
  const updateMutation = useUpdatePolicy();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Policy fields
  const [form, setForm] = useState({
    policyNumber: "",
    customerName: "",
    carrier: "",
    type: "",
    status: "active",
    substatus: "",
    // CSR-1604 — premium breakdown; `premium` is the derived Total.
    policyPremium: "",
    brokerFee: "",
    policyFees: "",
    taxes: "",
    commissionPct: "",
    producer: "",
    effectiveDate: "",
    expirationDate: "",
    dotNumber: "",
    companyEmail: "",
  });
  const [initialForm, setInitialForm] = useState<typeof form | null>(null);
  const [initialCustomCarrier, setInitialCustomCarrier] = useState("");
  const [customCarrier, setCustomCarrier] = useState("");
  const [tried, setTried] = useState(false);
  const [note, setNote] = useState("");

  // Coverage fields
  const [cov, setCov] = useState<Required<PolicyCoverages>>(defaultCoverages);
  const [jsonDirty, setJsonDirty] = useState({
    vehicles: false,
    trailers: false,
    drivers: false,
    coverages: false,
  });

  // Coverage enabled state (whether the policy has this coverage)
  const [covEnabled, setCovEnabled] = useState({
    generalLiability: true,
    autoLiability: true,
    nonTruckingLiability: false,
    hiredAutoLiability: false,
    hiredNonOwnedLiability: false,
    umbrella: false,
    workersComp: false,
    cargo: false,
    onHook: false,
    physicalDamage: false,
    trailerInterchange: false,
    nonOwnedTrailerPhysicalDamage: false,
  });

  // Vehicles
  const [vehicles, setVehicles] = useState<PolicyVehicle[]>([]);

  // Trailers
  // TODO(CSR-665 follow-up): expose trailer editor UI.
  const [trailers, setTrailers] = useState<PolicyTrailer[]>([]);

  // Drivers
  const [drivers, setDrivers] = useState<PolicyDriver[]>([]);

  // Coverage section toggles (expanded/collapsed)
  const [glOpen, setGlOpen] = useState(true);
  const [alOpen, setAlOpen] = useState(true);
  const [ntlOpen, setNtlOpen] = useState(false);
  const [haOpen, setHaOpen] = useState(false);
  const [hnoaOpen, setHnoaOpen] = useState(false);
  const [umbrellaOpen, setUmbrellaOpen] = useState(false);
  const [wcOpen, setWcOpen] = useState(false);
  const [cargoOpen, setCargoOpen] = useState(false);
  const [onHookOpen, setOnHookOpen] = useState(false);
  const [pdOpen, setPdOpen] = useState(false);
  const [tiOpen, setTiOpen] = useState(false);
  const [notpdOpen, setNotpdOpen] = useState(false);

  // Initialize form when policy loads
  useEffect(() => {
    if (policy) {
      const carrierNames = (carrierList ?? []).map((c) => c.name);
      const isKnown = carrierNames.includes(policy.carrier) || ["Other"].includes(policy.carrier);
      const nextForm = {
        policyNumber: policy.policyNumber,
        customerName: policy.customerName,
        carrier: isKnown ? policy.carrier : "Other",
        type: policy.type || "",
        status: policy.status || "active",
        substatus: policy.substatus ?? "",
        // CSR-1604 — hydrate the breakdown from stored components; a synced
        // policy with only a headline premium seeds Policy Premium from it.
        policyPremium: policy.policyPremium
          ? formatDollar(policy.policyPremium)
          : hasAnyBreakdown(policy)
            ? ""
            : policy.premium
              ? formatDollar(policy.premium)
              : "",
        brokerFee: policy.brokerFee ? formatDollar(policy.brokerFee) : "",
        policyFees: policy.policyFees ? formatDollar(policy.policyFees) : "",
        taxes: policy.taxes ? formatDollar(policy.taxes) : "",
        // CSR-1606 — stored value only; carrier defaults never re-apply here.
        commissionPct: policy.commissionPct ?? "",
        producer: policy.producer || "",
        effectiveDate: policy.effectiveDate ?? "",
        expirationDate: policy.expirationDate ?? "",
        dotNumber: policy.dotNumber || "",
        companyEmail: policy.companyEmail || "",
      };
      const nextCustomCarrier = isKnown ? "" : policy.carrier;
      setForm(nextForm);
      setInitialForm(nextForm);
      setCustomCarrier(nextCustomCarrier);
      setInitialCustomCarrier(nextCustomCarrier);
      const existingVehicles = policy.vehicles as PolicyVehicle[] | null;
      setVehicles(Array.isArray(existingVehicles) ? existingVehicles.map(v => ({ ...v })) : []);
      const existingTrailers = policy.trailers as PolicyTrailer[] | null;
      setTrailers(Array.isArray(existingTrailers) ? existingTrailers.map(t => ({ ...t })) : []);
      const existingDrivers = policy.drivers as PolicyDriver[] | null;
      setDrivers(
        Array.isArray(existingDrivers)
          ? existingDrivers.map((d) => {
              // Populate canonical `name` from firstName/lastName for legacy
              // DC rows so the editable form reflects the rendered name.
              const copy: PolicyDriver = { ...d };
              if (!copy.name) {
                const combined = [copy.firstName, copy.lastName]
                  .filter((s): s is string => Boolean(s && s.trim()))
                  .map((s) => s.trim())
                  .join(" ")
                  .trim();
                if (combined) copy.name = combined;
              }
              return copy;
            })
          : []
      );
      const existing = policy.coverages as PolicyCoverages | null;
      setCov(mergeCoverages(existing));
      setCovEnabled({
        generalLiability: !!existing?.generalLiability,
        autoLiability: !!existing?.autoLiability,
        nonTruckingLiability: !!existing?.nonTruckingLiability,
        hiredAutoLiability: !!existing?.hiredAutoLiability,
        hiredNonOwnedLiability: !!existing?.hiredNonOwnedLiability,
        umbrella: !!existing?.umbrella,
        workersComp: !!existing?.workersComp,
        cargo: !!existing?.cargo,
        onHook: !!existing?.onHook,
        physicalDamage: !!existing?.physicalDamage,
        trailerInterchange: !!existing?.trailerInterchange,
        nonOwnedTrailerPhysicalDamage: !!existing?.nonOwnedTrailerPhysicalDamage,
      });
      setJsonDirty({
        vehicles: false,
        trailers: false,
        drivers: false,
        coverages: false,
      });
      // Auto-expand enabled sections
      setGlOpen(!!existing?.generalLiability);
      setAlOpen(!!existing?.autoLiability);
      setNtlOpen(!!existing?.nonTruckingLiability);
      setHaOpen(!!existing?.hiredAutoLiability);
      setHnoaOpen(!!existing?.hiredNonOwnedLiability);
      setUmbrellaOpen(!!existing?.umbrella);
      setWcOpen(!!existing?.workersComp);
      setCargoOpen(!!existing?.cargo);
      setOnHookOpen(!!existing?.onHook);
      setPdOpen(!!existing?.physicalDamage);
      setTiOpen(!!existing?.trailerInterchange);
      setNotpdOpen(!!existing?.nonOwnedTrailerPhysicalDamage);
    }
  }, [policy, carrierList]);

  const carrierOptions = useMemo(() => {
    const names = (carrierList ?? []).map((c) => c.name);
    const priorityOrder = ["Progressive", "Geico", "CoverWhale"];
    const priority = priorityOrder.filter((c) => names.includes(c));
    const rest = names
      .filter((c) => !priorityOrder.includes(c))
      .sort((a, b) => a.localeCompare(b));
    return [...priority, ...rest, "Other"];
  }, [carrierList]);

  const effectiveCarrier = form.carrier === "Other" ? customCarrier.trim() : form.carrier;
  const errors = getPolicyErrors({ ...form, carrier: effectiveCarrier });
  const errClass = (field: string) => (tried && errors[field] ? "border-destructive" : "");

  const toggleCoverage = (section: keyof typeof covEnabled) => {
    const newEnabled = !covEnabled[section];
    setJsonDirty((prev) => ({ ...prev, coverages: true }));
    setCovEnabled((prev) => ({ ...prev, [section]: newEnabled }));
    if (newEnabled) {
      // Auto-expand when enabling
      const setters: Record<string, (v: boolean) => void> = {
        generalLiability: setGlOpen, autoLiability: setAlOpen, nonTruckingLiability: setNtlOpen,
        hiredAutoLiability: setHaOpen, hiredNonOwnedLiability: setHnoaOpen,
        umbrella: setUmbrellaOpen, workersComp: setWcOpen, cargo: setCargoOpen,
        onHook: setOnHookOpen, physicalDamage: setPdOpen, trailerInterchange: setTiOpen,
        nonOwnedTrailerPhysicalDamage: setNotpdOpen,
      };
      setters[section]?.(true);
    }
  };

  const setCovField = (section: keyof Required<PolicyCoverages>, field: string, value: string | boolean) => {
    setJsonDirty((prev) => ({ ...prev, coverages: true }));
    setCov((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const addVehicle = () => {
    setJsonDirty((prev) => ({ ...prev, vehicles: true }));
    setVehicles((prev) => [...prev, { vin: "", year: "", make: "", model: "", truckType: "" }]);
  };

  const [customTruckType, setCustomTruckType] = useState<Set<number>>(new Set());

  const removeVehicle = (index: number) => {
    setJsonDirty((prev) => ({ ...prev, vehicles: true }));
    setVehicles((prev) => prev.filter((_, i) => i !== index));
    setCustomTruckType((prev) => {
      const next = new Set<number>();
      Array.from(prev).forEach((idx) => {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      });
      return next;
    });
  };

  const setVehicleField = (index: number, field: keyof PolicyVehicle, value: string) => {
    setJsonDirty((prev) => ({ ...prev, vehicles: true }));
    setVehicles((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    );
  };

  const addDriver = () => {
    setJsonDirty((prev) => ({ ...prev, drivers: true }));
    setDrivers((prev) => [...prev, { name: "", licenseNumber: "", licenseState: "", dateOfBirth: "", dateOfHire: "", yearsExperience: "" }]);
  };

  const removeDriver = (index: number) => {
    setJsonDirty((prev) => ({ ...prev, drivers: true }));
    setDrivers((prev) => prev.filter((_, i) => i !== index));
  };

  const setDriverField = (index: number, field: keyof PolicyDriver, value: string) => {
    setJsonDirty((prev) => ({ ...prev, drivers: true }));
    setDrivers((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)),
    );
  };

  const handleSubmit = async () => {
    setTried(true);
    if (hasErrors(errors)) {
      scrollToFirstError();
      return;
    }

    const carrierValue = form.carrier === "Other" ? customCarrier.trim() : form.carrier;

    // CSR-1604 — unformat the breakdown and derive the headline Total.
    const breakdown = {
      policyPremium: form.policyPremium ? unformatDollar(form.policyPremium) : null,
      brokerFee: form.brokerFee ? unformatDollar(form.brokerFee) : null,
      policyFees: form.policyFees ? unformatDollar(form.policyFees) : null,
      taxes: form.taxes ? unformatDollar(form.taxes) : null,
    };
    const totalPremium = computeTotalPremium(breakdown);
    const existingCoverages = policy?.coverages as PolicyCoverages | null | undefined;
    const coverages = {
      generalLiability: covEnabled.generalLiability ? cov.generalLiability : null,
      autoLiability: covEnabled.autoLiability ? cov.autoLiability : null,
      nonTruckingLiability: covEnabled.nonTruckingLiability ? cov.nonTruckingLiability : null,
      hiredAutoLiability: covEnabled.hiredAutoLiability ? cov.hiredAutoLiability : null,
      hiredNonOwnedLiability: covEnabled.hiredNonOwnedLiability
        ? cov.hiredNonOwnedLiability
        : null,
      umbrella: covEnabled.umbrella ? cov.umbrella : null,
      workersComp: covEnabled.workersComp ? cov.workersComp : null,
      cargo: covEnabled.cargo ? cov.cargo : null,
      onHook: covEnabled.onHook ? cov.onHook : null,
      physicalDamage: covEnabled.physicalDamage ? cov.physicalDamage : null,
      trailerInterchange: covEnabled.trailerInterchange ? cov.trailerInterchange : null,
      nonOwnedTrailerPhysicalDamage: covEnabled.nonOwnedTrailerPhysicalDamage
        ? cov.nonOwnedTrailerPhysicalDamage
        : null,
      rentalReimbursement: existingCoverages?.rentalReimbursement
        ? cov.rentalReimbursement
        : null,
      nonOwnedAuto: existingCoverages?.nonOwnedAuto ? cov.nonOwnedAuto : null,
    };
    const allScalarData: Record<string, unknown> = {
      policyNumber: form.policyNumber,
      customerName: form.customerName,
      carrier: carrierValue,
      type: form.type || null,
      status: form.status,
      substatus: form.substatus || null,
      ...breakdown,
      commissionPct: form.commissionPct.trim() || null,
      premium: totalPremium || null,
      producer: form.producer || null,
      effectiveDate: form.effectiveDate,
      expirationDate: form.expirationDate,
      dotNumber: form.dotNumber || null,
      companyEmail: form.companyEmail || null,
    };
    const initialCarrierValue =
      initialForm?.carrier === "Other" ? initialCustomCarrier : initialForm?.carrier;
    const initialBreakdown = initialForm
      ? {
          policyPremium: initialForm.policyPremium
            ? unformatDollar(initialForm.policyPremium)
            : null,
          brokerFee: initialForm.brokerFee ? unformatDollar(initialForm.brokerFee) : null,
          policyFees: initialForm.policyFees ? unformatDollar(initialForm.policyFees) : null,
          taxes: initialForm.taxes ? unformatDollar(initialForm.taxes) : null,
        }
      : null;
    const initialScalarData: Record<string, unknown> | null =
      initialForm && initialBreakdown
        ? {
            policyNumber: initialForm.policyNumber,
            customerName: initialForm.customerName,
            carrier: initialCarrierValue,
            type: initialForm.type || null,
            status: initialForm.status,
            substatus: initialForm.substatus || null,
            ...initialBreakdown,
            commissionPct: initialForm.commissionPct.trim() || null,
            premium: computeTotalPremium(initialBreakdown) || null,
            producer: initialForm.producer || null,
            effectiveDate: initialForm.effectiveDate,
            expirationDate: initialForm.expirationDate,
            dotNumber: initialForm.dotNumber || null,
            companyEmail: initialForm.companyEmail || null,
          }
        : null;
    const data: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(allScalarData)) {
      if (!initialScalarData || initialScalarData[field] !== value) {
        data[field] = value;
      }
    }
    if (jsonDirty.vehicles) data.vehicles = vehicles.length > 0 ? vehicles : null;
    if (jsonDirty.trailers) data.trailers = trailers.length > 0 ? trailers : null;
    if (jsonDirty.drivers) data.drivers = drivers.length > 0 ? drivers : null;
    if (jsonDirty.coverages) data.coverages = coverages;

    try {
      await updateMutation.mutateAsync({
        id: policyId,
        data: data as any,
        note: note.trim() || undefined,
      });
      toast({ title: "Policy Updated", description: "Policy has been updated successfully." });
      navigate(`/policies/${policyId}`);
    } catch (err: unknown) {
      const description =
        err instanceof Error ? err.message : "Could not update the policy.";
      toast({ title: "Update Failed", description, variant: "destructive" });
    }
  };

  const dollarField = (
    section: keyof Required<PolicyCoverages>,
    field: string,
    label: string,
    value: string,
    id: string,
  ) => (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        placeholder="e.g. $1,000,000"
        value={value}
        onChange={(e) => setCovField(section, field, formatDollar(e.target.value))}
        maxLength={20}
      />
    </div>
  );

  const sectionToggle = (
    label: string,
    section: keyof typeof covEnabled,
    isOpen: boolean,
    toggle: () => void,
  ) => (
    <div className="flex items-center justify-between w-full p-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`cov-enable-${section}`}
          checked={covEnabled[section]}
          onCheckedChange={() => toggleCoverage(section)}
        />
        <Label
          htmlFor={`cov-enable-${section}`}
          className={cn("text-sm font-semibold cursor-pointer", !covEnabled[section] && "text-muted-foreground")}
        >
          {label}
        </Label>
      </div>
      {covEnabled[section] && (
        <button
          type="button"
          className="hover:bg-muted/50 rounded p-1 cursor-pointer"
          onClick={toggle}
        >
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-96 bg-muted animate-pulse rounded-lg" />
        </div>
      </Layout>
    );
  }

  if (isError || !policy) {
    return (
      <Layout>
        <div className="space-y-6">
          <Button variant="ghost" className="gap-2 pl-0 hover:pl-2 transition-all" onClick={() => window.history.back()}>
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg font-medium">Policy not found</p>
              <p className="text-sm text-muted-foreground mt-1">The policy you're looking for doesn't exist or has been removed.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              data-testid="button-back"
              onClick={() => navigate(`/policies/${policyId}`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading text-foreground tracking-tight">Edit Policy</h1>
              <p className="text-muted-foreground mt-0.5">{policy.policyNumber} — {policy.customerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/policies/${policyId}`)} data-testid="button-cancel-edit">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={updateMutation.isPending} className="gap-2" data-testid="button-save-policy">
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Policy Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <FileText className="w-4 h-4" /> Policy Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-policyNumber">Policy Number *</Label>
                  <Input
                    id="edit-policyNumber"
                    value={form.policyNumber}
                    onChange={(e) => setForm({ ...form, policyNumber: formatPolicyNumber(e.target.value) })}
                    placeholder="POL-12345"
                    maxLength={30}
                    aria-invalid={tried && errors.policyNumber}
                    className={errClass("policyNumber")}
                    data-testid="edit-input-policy-number"
                  />
                  <FieldError show={tried && errors.policyNumber} message="Policy number is required" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-customerName">Customer Name *</Label>
                  <Input
                    id="edit-customerName"
                    value={form.customerName}
                    onChange={(e) => setForm({ ...form, customerName: formatCompanyName(e.target.value) })}
                    placeholder="Acme Trucking LLC"
                    maxLength={200}
                    aria-invalid={tried && errors.customerName}
                    className={errClass("customerName")}
                    data-testid="edit-input-customer-name"
                  />
                  <FieldError show={tried && errors.customerName} message="Customer name is required" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-carrier">Carrier *</Label>
                  <Select
                    value={form.carrier}
                    onValueChange={(value) => {
                      setForm({ ...form, carrier: value });
                      if (value !== "Other") setCustomCarrier("");
                    }}
                  >
                    <SelectTrigger
                      id="edit-carrier"
                      aria-invalid={tried && errors.carrier}
                      className={errClass("carrier")}
                      data-testid="edit-select-carrier"
                    >
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {carrierOptions.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError show={tried && errors.carrier} message="Carrier is required" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-policyType">Policy Type</Label>
                  <Select value={form.type} onValueChange={(value) => setForm({ ...form, type: value })}>
                    <SelectTrigger id="edit-policyType" data-testid="edit-select-policy-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {POLICY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.carrier === "Other" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    value={customCarrier}
                    onChange={(e) => setCustomCarrier(e.target.value)}
                    placeholder="Enter carrier name"
                    maxLength={200}
                    aria-invalid={tried && errors.carrier}
                    className={cn("-mt-2", errClass("carrier"))}
                    data-testid="edit-input-custom-carrier"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                    <SelectTrigger id="edit-status" data-testid="edit-select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POLICY_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-substatus">Substatus</Label>
                  <Select
                    value={form.substatus || SUBSTATUS_NONE}
                    onValueChange={(value) =>
                      setForm({ ...form, substatus: value === SUBSTATUS_NONE ? "" : value })
                    }
                  >
                    <SelectTrigger id="edit-substatus" data-testid="edit-select-substatus">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SUBSTATUS_NONE}>None</SelectItem>
                      {POLICY_SUBSTATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-premium">Policy Premium</Label>
                  <Input
                    id="edit-premium"
                    value={form.policyPremium}
                    onChange={(e) => setForm({ ...form, policyPremium: formatDollar(e.target.value) })}
                    placeholder="$10,000"
                    data-testid="edit-input-premium"
                  />
                </div>
              </div>
              {/* CSR-1604 — premium breakdown; Total recalculates live. */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-broker-fee">Broker Fee</Label>
                  <Input
                    id="edit-broker-fee"
                    value={form.brokerFee}
                    onChange={(e) => setForm({ ...form, brokerFee: formatDollar(e.target.value) })}
                    placeholder="$0"
                    data-testid="edit-input-broker-fee"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-policy-fees">Finance Charge</Label>
                  <Input
                    id="edit-policy-fees"
                    value={form.policyFees}
                    onChange={(e) => setForm({ ...form, policyFees: formatDollar(e.target.value) })}
                    placeholder="$0"
                    data-testid="edit-input-policy-fees"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-taxes">Taxes</Label>
                  <Input
                    id="edit-taxes"
                    value={form.taxes}
                    onChange={(e) => setForm({ ...form, taxes: formatDollar(e.target.value) })}
                    placeholder="$0"
                    data-testid="edit-input-taxes"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-commission">Commission (%)</Label>
                  <Input
                    id="edit-commission"
                    inputMode="decimal"
                    value={form.commissionPct}
                    onChange={(e) => setForm({ ...form, commissionPct: e.target.value })}
                    placeholder="0"
                    data-testid="edit-input-commission"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-total-premium">Total Premium</Label>
                  <Input
                    id="edit-total-premium"
                    readOnly
                    tabIndex={-1}
                    className="bg-muted"
                    value={
                      (() => {
                        const total = computeTotalPremium({
                          policyPremium: unformatDollar(form.policyPremium),
                          brokerFee: unformatDollar(form.brokerFee),
                          policyFees: unformatDollar(form.policyFees),
                          taxes: unformatDollar(form.taxes),
                        });
                        return total ? formatDollar(total.replace(/\.00$/, "")) : "$0";
                      })()
                    }
                    data-testid="edit-input-total-premium"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-producer">Producer</Label>
                <SearchableSelect
                  value={form.producer}
                  onChange={(v) => setForm({ ...form, producer: v })}
                  options={producerOptions}
                  allowCustom
                  placeholder="Select or type a producer…"
                />
                {/* Test-only handle: drives state without going through Radix
                    popover (hard to drive in happy-dom). */}
                <input
                  type="text"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  data-testid="edit-input-producer"
                  value={form.producer}
                  onChange={(e) => setForm({ ...form, producer: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-effectiveDate">Effective Date *</Label>
                  <Input
                    id="edit-effectiveDate"
                    type="date"
                    value={form.effectiveDate}
                    onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                    aria-invalid={tried && errors.effectiveDate}
                    className={errClass("effectiveDate")}
                    data-testid="edit-input-effective-date"
                  />
                  <FieldError show={tried && errors.effectiveDate} message="Effective date is required" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-expirationDate">Expiration Date *</Label>
                  <Input
                    id="edit-expirationDate"
                    type="date"
                    value={form.expirationDate}
                    onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                    aria-invalid={tried && errors.expirationDate}
                    className={errClass("expirationDate")}
                    data-testid="edit-input-expiration-date"
                  />
                  <FieldError
                    show={tried && errors.expirationDate}
                    message={!form.expirationDate ? "Expiration date is required" : "Expiration date must be after effective date"}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-dotNumber">DOT Number</Label>
                  <Input
                    id="edit-dotNumber"
                    value={form.dotNumber}
                    onChange={(e) => setForm({ ...form, dotNumber: e.target.value })}
                    placeholder="1234567"
                    maxLength={20}
                    data-testid="edit-input-dot-number"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-companyEmail">Company Email</Label>
                  <Input
                    id="edit-companyEmail"
                    type="email"
                    value={form.companyEmail}
                    onChange={(e) => setForm({ ...form, companyEmail: e.target.value })}
                    placeholder="company@example.com"
                    maxLength={200}
                    data-testid="edit-input-company-email"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coverages & Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Shield className="w-4 h-4" /> Coverages & Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {/* General Liability */}
              <div className={cn("border rounded-lg", !covEnabled.generalLiability && "opacity-60")}>
                {sectionToggle("Commercial General Liability", "generalLiability", glOpen, () => setGlOpen(!glOpen))}
                {covEnabled.generalLiability && glOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("generalLiability", "eachOccurrence", "Each Occurrence", cov.generalLiability.eachOccurrence, "cov-gl-each-occurrence")}
                      {dollarField("generalLiability", "damageToRented", "Damage to Rented Premises", cov.generalLiability.damageToRented, "cov-gl-damage-rented")}
                      {dollarField("generalLiability", "medExp", "Med Exp (Any one person)", cov.generalLiability.medExp, "cov-gl-med-exp")}
                      {dollarField("generalLiability", "personalAdvInjury", "Personal & Adv Injury", cov.generalLiability.personalAdvInjury, "cov-gl-personal-adv")}
                      {dollarField("generalLiability", "generalAggregate", "General Aggregate", cov.generalLiability.generalAggregate, "cov-gl-general-agg")}
                      {dollarField("generalLiability", "productsCompOpAgg", "Products-Comp/Op Agg", cov.generalLiability.productsCompOpAgg, "cov-gl-products-comp")}
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="cov-gl-occurrence"
                          checked={!cov.generalLiability.claimsMade}
                          onCheckedChange={() => setCovField("generalLiability", "claimsMade", false)}
                        />
                        <Label htmlFor="cov-gl-occurrence" className="text-xs cursor-pointer">
                          Occurrence
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="cov-gl-claims-made"
                          checked={cov.generalLiability.claimsMade}
                          onCheckedChange={() => setCovField("generalLiability", "claimsMade", true)}
                        />
                        <Label htmlFor="cov-gl-claims-made" className="text-xs cursor-pointer">
                          Claims Made
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="cov-gl-aggregate-per" className="text-xs whitespace-nowrap">
                          Aggregate Applies Per
                        </Label>
                        <select
                          id="cov-gl-aggregate-per"
                          value={cov.generalLiability.aggregateAppliesPer}
                          onChange={(e) => setCovField("generalLiability", "aggregateAppliesPer", e.target.value)}
                          className="flex h-8 w-28 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          {AGGREGATE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto Liability */}
              <div className={cn("border rounded-lg", !covEnabled.autoLiability && "opacity-60")}>
                {sectionToggle("Automobile Liability", "autoLiability", alOpen, () => setAlOpen(!alOpen))}
                {covEnabled.autoLiability && alOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("autoLiability", "combinedSingleLimit", "Combined Single Limit", cov.autoLiability.combinedSingleLimit, "cov-al-combined")}
                      {dollarField("autoLiability", "bodilyInjuryPerPerson", "Bodily Injury (Per Person)", cov.autoLiability.bodilyInjuryPerPerson, "cov-al-bi-person")}
                      {dollarField("autoLiability", "bodilyInjuryPerAccident", "Bodily Injury (Per Accident)", cov.autoLiability.bodilyInjuryPerAccident, "cov-al-bi-accident")}
                      {dollarField("autoLiability", "propertyDamage", "Property Damage", cov.autoLiability.propertyDamage, "cov-al-property-damage")}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="cov-al-type" className="text-xs whitespace-nowrap">
                        Auto Type
                      </Label>
                      <select
                        id="cov-al-type"
                        value={cov.autoLiability.autoType}
                        onChange={(e) => setCovField("autoLiability", "autoType", e.target.value)}
                        className="flex h-8 w-32 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        {AUTO_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Non-Trucking Liability */}
              <div className={cn("border rounded-lg", !covEnabled.nonTruckingLiability && "opacity-60")}>
                {sectionToggle("Non-Trucking Liability", "nonTruckingLiability", ntlOpen, () => setNtlOpen(!ntlOpen))}
                {covEnabled.nonTruckingLiability && ntlOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("nonTruckingLiability", "limit", "Limit", cov.nonTruckingLiability.limit, "cov-ntl-limit")}
                    </div>
                  </div>
                )}
              </div>

              {/* Hired Auto Liability */}
              <div className={cn("border rounded-lg", !covEnabled.hiredAutoLiability && "opacity-60")}>
                {sectionToggle("Hired Auto Liability", "hiredAutoLiability", haOpen, () => setHaOpen(!haOpen))}
                {covEnabled.hiredAutoLiability && haOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("hiredAutoLiability", "combinedSingleLimit", "Combined Single Limit", cov.hiredAutoLiability.combinedSingleLimit, "cov-ha-csl")}
                    </div>
                  </div>
                )}
              </div>

              {/* Hired & Non-Owned Auto Liability */}
              <div className={cn("border rounded-lg", !covEnabled.hiredNonOwnedLiability && "opacity-60")}>
                {sectionToggle("Hired & Non-Owned Auto Liability", "hiredNonOwnedLiability", hnoaOpen, () => setHnoaOpen(!hnoaOpen))}
                {covEnabled.hiredNonOwnedLiability && hnoaOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("hiredNonOwnedLiability", "limit", "Limit", cov.hiredNonOwnedLiability.limit, "cov-hnoa-limit")}
                    </div>
                  </div>
                )}
              </div>

              {/* Umbrella / Excess Liability */}
              <div className={cn("border rounded-lg", !covEnabled.umbrella && "opacity-60")}>
                {sectionToggle("Umbrella / Excess Liability", "umbrella", umbrellaOpen, () => setUmbrellaOpen(!umbrellaOpen))}
                {covEnabled.umbrella && umbrellaOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("umbrella", "eachOccurrence", "Each Occurrence", cov.umbrella.eachOccurrence, "cov-umb-each-occurrence")}
                      {dollarField("umbrella", "aggregate", "Aggregate", cov.umbrella.aggregate, "cov-umb-aggregate")}
                      {dollarField("umbrella", "deductible", "Deductible", cov.umbrella.deductible, "cov-umb-deductible")}
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cov-umb-excess"
                        checked={cov.umbrella.isExcess}
                        onCheckedChange={(checked) => setCovField("umbrella", "isExcess", !!checked)}
                      />
                      <Label htmlFor="cov-umb-excess" className="text-xs cursor-pointer">
                        Excess Liability
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Workers Compensation */}
              <div className={cn("border rounded-lg", !covEnabled.workersComp && "opacity-60")}>
                {sectionToggle("Workers Compensation", "workersComp", wcOpen, () => setWcOpen(!wcOpen))}
                {covEnabled.workersComp && wcOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        id="cov-wc-statute"
                        checked={cov.workersComp.perStatute}
                        onCheckedChange={(checked) => setCovField("workersComp", "perStatute", !!checked)}
                      />
                      <Label htmlFor="cov-wc-statute" className="text-xs cursor-pointer">
                        Per Statute
                      </Label>
                    </div>
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("workersComp", "elEachAccident", "E.L. Each Accident", cov.workersComp.elEachAccident, "cov-wc-each-accident")}
                      {dollarField("workersComp", "elDiseaseEaEmployee", "E.L. Disease - Ea Employee", cov.workersComp.elDiseaseEaEmployee, "cov-wc-disease-employee")}
                      {dollarField("workersComp", "elDiseasePolicyLimit", "E.L. Disease - Policy Limit", cov.workersComp.elDiseasePolicyLimit, "cov-wc-disease-policy")}
                    </div>
                  </div>
                )}
              </div>

              {/* Cargo */}
              <div className={cn("border rounded-lg", !covEnabled.cargo && "opacity-60")}>
                {sectionToggle("Motor Truck Cargo", "cargo", cargoOpen, () => setCargoOpen(!cargoOpen))}
                {covEnabled.cargo && cargoOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("cargo", "limit", "Limit", cov.cargo.limit, "cov-cargo-limit")}
                      {dollarField("cargo", "deductible", "Deductible", cov.cargo.deductible, "cov-cargo-deductible")}
                    </div>
                  </div>
                )}
              </div>

              {/* On-Hook */}
              <div className={cn("border rounded-lg", !covEnabled.onHook && "opacity-60")}>
                {sectionToggle("On-Hook", "onHook", onHookOpen, () => setOnHookOpen(!onHookOpen))}
                {covEnabled.onHook && onHookOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("onHook", "limit", "Limit", cov.onHook.limit, "cov-onhook-limit")}
                      {dollarField("onHook", "deductible", "Deductible", cov.onHook.deductible, "cov-onhook-deductible")}
                    </div>
                  </div>
                )}
              </div>

              {/* Physical Damage */}
              <div className={cn("border rounded-lg", !covEnabled.physicalDamage && "opacity-60")}>
                {sectionToggle("Physical Damage", "physicalDamage", pdOpen, () => setPdOpen(!pdOpen))}
                {covEnabled.physicalDamage && pdOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("physicalDamage", "comprehensiveDeductible", "Comprehensive Deductible", cov.physicalDamage.comprehensiveDeductible, "cov-pd-comprehensive")}
                      {dollarField("physicalDamage", "collisionDeductible", "Collision Deductible", cov.physicalDamage.collisionDeductible, "cov-pd-collision")}
                    </div>
                  </div>
                )}
              </div>

              {/* Trailer Interchange */}
              <div className={cn("border rounded-lg", !covEnabled.trailerInterchange && "opacity-60")}>
                {sectionToggle("Trailer Interchange", "trailerInterchange", tiOpen, () => setTiOpen(!tiOpen))}
                {covEnabled.trailerInterchange && tiOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("trailerInterchange", "limit", "Limit", cov.trailerInterchange.limit, "cov-ti-limit")}
                      {dollarField("trailerInterchange", "deductible", "Deductible", cov.trailerInterchange.deductible, "cov-ti-deductible")}
                    </div>
                  </div>
                )}
              </div>

              {/* Non-Owned Trailer Physical Damage */}
              <div className={cn("border rounded-lg", !covEnabled.nonOwnedTrailerPhysicalDamage && "opacity-60")}>
                {sectionToggle("Non-Owned Trailer Physical Damage", "nonOwnedTrailerPhysicalDamage", notpdOpen, () => setNotpdOpen(!notpdOpen))}
                {covEnabled.nonOwnedTrailerPhysicalDamage && notpdOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      {dollarField("nonOwnedTrailerPhysicalDamage", "limit", "Limit", cov.nonOwnedTrailerPhysicalDamage.limit, "cov-notpd-limit")}
                      {dollarField("nonOwnedTrailerPhysicalDamage", "deductible", "Deductible", cov.nonOwnedTrailerPhysicalDamage.deductible, "cov-notpd-deductible")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicles */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Truck className="w-4 h-4" /> Vehicles
              {vehicles.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">({vehicles.length})</span>
              )}
            </CardTitle>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addVehicle}>
              <Plus className="w-3.5 h-3.5" /> Add Vehicle
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {vehicles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No vehicles. Click "Add Vehicle" to add one.</p>
            ) : (
              <div className="space-y-3">
                {vehicles.map((v, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-muted-foreground">Vehicle {i + 1}</span>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive p-1 rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => removeVehicle(i)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      <div className="space-y-1">
                        <Label htmlFor={`vehicle-${i}-year`} className="text-xs">Year</Label>
                        <Input
                          id={`vehicle-${i}-year`}
                          value={String(v.year || "")}
                          onChange={(e) => setVehicleField(i, "year", e.target.value)}
                          placeholder="2024"
                          maxLength={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`vehicle-${i}-make`} className="text-xs">Make</Label>
                        <Input
                          id={`vehicle-${i}-make`}
                          value={v.make || ""}
                          onChange={(e) => setVehicleField(i, "make", e.target.value)}
                          placeholder="Freightliner"
                          maxLength={50}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`vehicle-${i}-model`} className="text-xs">Model</Label>
                        <Input
                          id={`vehicle-${i}-model`}
                          value={v.model || ""}
                          onChange={(e) => setVehicleField(i, "model", e.target.value)}
                          placeholder="Cascadia"
                          maxLength={50}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`vehicle-${i}-truckType`} className="text-xs">Truck Type</Label>
                        {customTruckType.has(i) || (v.truckType && !(TRUCK_TYPES as readonly string[]).includes(v.truckType)) ? (
                          <div className="flex gap-1.5">
                            <Input
                              id={`vehicle-${i}-truckType`}
                              value={v.truckType || ""}
                              onChange={(e) => setVehicleField(i, "truckType", e.target.value)}
                              placeholder="Enter truck type"
                              maxLength={50}
                              className="h-9"
                              autoFocus={customTruckType.has(i)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-9 px-2 text-xs text-muted-foreground"
                              onClick={() => {
                                setVehicleField(i, "truckType", "");
                                setCustomTruckType((prev) => { const next = new Set(prev); next.delete(i); return next; });
                              }}
                            >
                              Back
                            </Button>
                          </div>
                        ) : (
                          <Select
                            value={v.truckType || ""}
                            onValueChange={(val) => {
                              if (val === "__other__") {
                                setVehicleField(i, "truckType", "");
                                setCustomTruckType((prev) => new Set(prev).add(i));
                              } else {
                                setVehicleField(i, "truckType", val);
                              }
                            }}
                          >
                            <SelectTrigger id={`vehicle-${i}-truckType`} className="h-9">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {TRUCK_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                              <SelectItem value="__other__">Other...</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`vehicle-${i}-vin`} className="text-xs">VIN</Label>
                        <Input
                          id={`vehicle-${i}-vin`}
                          value={v.vin || ""}
                          onChange={(e) => setVehicleField(i, "vin", e.target.value.toUpperCase())}
                          placeholder="1FUJGLDV8CLBP8834"
                          maxLength={17}
                          className="font-mono"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drivers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Users className="w-4 h-4" /> Drivers
              {drivers.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">({drivers.length})</span>
              )}
            </CardTitle>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addDriver}>
              <Plus className="w-3.5 h-3.5" /> Add Driver
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {drivers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No drivers. Click "Add Driver" to add one.</p>
            ) : (
              <div className="space-y-3">
                {drivers.map((d, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-muted-foreground">Driver {i + 1}</span>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive p-1 rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => removeDriver(i)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-name`} className="text-xs">Name</Label>
                        <Input
                          id={`driver-${i}-name`}
                          value={d.name || ""}
                          onChange={(e) => setDriverField(i, "name", e.target.value)}
                          placeholder="John Smith"
                          maxLength={100}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-licenseNumber`} className="text-xs">License #</Label>
                        <Input
                          id={`driver-${i}-licenseNumber`}
                          value={d.licenseNumber || ""}
                          onChange={(e) => setDriverField(i, "licenseNumber", e.target.value.toUpperCase())}
                          placeholder="CDL12345"
                          maxLength={20}
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-licenseState`} className="text-xs">License State</Label>
                        <Select
                          value={d.licenseState || ""}
                          onValueChange={(val) => setDriverField(i, "licenseState", val)}
                        >
                          <SelectTrigger id={`driver-${i}-licenseState`} className="h-9">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.value} — {s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-dateOfBirth`} className="text-xs">Date of Birth</Label>
                        <Input
                          id={`driver-${i}-dateOfBirth`}
                          type="date"
                          value={toIsoDate(d.dateOfBirth || "")}
                          onChange={(e) => setDriverField(i, "dateOfBirth", fromIsoDate(e.target.value))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-dateOfHire`} className="text-xs">Hire Date</Label>
                        <Input
                          id={`driver-${i}-dateOfHire`}
                          type="date"
                          value={toIsoDate(d.dateOfHire || "")}
                          onChange={(e) => setDriverField(i, "dateOfHire", fromIsoDate(e.target.value))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`driver-${i}-yearsExperience`} className="text-xs">Years Experience</Label>
                        <Input
                          id={`driver-${i}-yearsExperience`}
                          type="number"
                          value={String(d.yearsExperience || "")}
                          onChange={(e) => setDriverField(i, "yearsExperience", e.target.value)}
                          placeholder="5"
                          min={0}
                          max={99}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Note */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-2">
              <Label htmlFor="edit-note">Change Note (optional)</Label>
              <Textarea
                id="edit-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Reason for this change..."
                maxLength={500}
                rows={2}
                data-testid="edit-input-change-note"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bottom action bar */}
        <div className="flex items-center justify-end gap-2 pb-6">
          <Button variant="outline" onClick={() => navigate(`/policies/${policyId}`)} data-testid="button-cancel-edit-bottom">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending} className="gap-2" data-testid="button-save-policy-bottom">
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
